import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';

import {
  getCartsFromDigitisationId,
  getDigitisationId,
  modifyCartData,
  placeFailedOrderCart,
  placeOrderByCartId,
  addSubstituteToCart,
  getPartnerStoresByCartId,
  getDetailsForApollocart
} from './UploadRxComponents/Services';

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  primary:       '#6C5DD3',
  primaryLight:  '#F5F4FF',
  primaryBorder: '#CFC8FF',
  text:          '#0F0B28',
  muted:         '#808191',
  border:        '#E4E4E4',
  surface:       '#FAFAFA',
  white:         '#FFFFFF',
  pageBg:        '#F4F4F8',
  success:       '#27AE60',
  successLight:  '#E6F9F0',
  danger:        '#EB5757',
  dangerLight:   '#FFF0F0',
  warning:       '#FF754C',
  warningLight:  '#FFF3DC',
  shadow:        '0 1px 4px rgba(0,0,0,0.07)',
  shadowMd:      '0 4px 12px rgba(0,0,0,0.10)',
};

const SHIMMER_CSS = `
  @keyframes shimmer {
    0%   { background-position: -400px 0 }
    100% { background-position:  400px 0 }
  }
  .ro-skeleton {
    background: linear-gradient(90deg, #EFEFEF 25%, #E3E3E3 50%, #EFEFEF 75%);
    background-size: 800px 100%;
    animation: shimmer 1.4s infinite linear;
    border-radius: 6px;
  }
`;

// ── Micro-components ──────────────────────────────────────────────────────────

const SectionCard = ({ title, children, style }) => (
  <div style={{
    background: T.white,
    borderRadius: 12,
    border: `1px solid ${T.border}`,
    boxShadow: T.shadow,
    marginBottom: 20,
    overflow: 'hidden',
    ...style,
  }}>
    {title && (
      <div style={{
        padding: '13px 20px',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
        <div style={{ width: 3, height: 16, borderRadius: 2, background: T.primary, flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: T.text, letterSpacing: '0.01em' }}>{title}</span>
      </div>
    )}
    <div style={{ padding: 20 }}>{children}</div>
  </div>
);

const Badge = ({ children, variant = 'default' }) => {
  const map = {
    default: { bg: '#F2F4F7', color: T.muted },
    primary: { bg: T.primaryLight, color: T.primary },
    success: { bg: T.successLight, color: T.success },
    warning: { bg: T.warningLight, color: T.warning },
    danger:  { bg: T.dangerLight,  color: T.danger  },
    blue:    { bg: '#E8F3FF', color: '#0066CC' },
  };
  const v = map[variant] || map.default;
  return (
    <span style={{
      display: 'inline-block',
      background: v.bg,
      color: v.color,
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 8px',
      borderRadius: 100,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
};

const PrimaryBtn = ({ children, disabled, onClick, style }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      background: disabled ? '#C4BEF5' : T.primary,
      color: T.white,
      border: 'none',
      borderRadius: 8,
      padding: '10px 24px',
      fontSize: 13,
      fontWeight: 700,
      cursor: disabled ? 'not-allowed' : 'pointer',
      letterSpacing: '0.01em',
      transition: 'background 0.15s',
      ...style,
    }}
  >
    {children}
  </button>
);

const QtyBtn = ({ children, onClick, primary }) => (
  <button
    onClick={onClick}
    style={{
      width: 28,
      height: 28,
      borderRadius: 6,
      border: `1.5px solid ${primary ? T.primary : T.border}`,
      background: primary ? T.primaryLight : T.white,
      color: primary ? T.primary : T.text,
      fontSize: 16,
      fontWeight: 700,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      flexShrink: 0,
    }}
  >
    {children}
  </button>
);

const Divider = () => (
  <div style={{ height: 1, background: T.border, margin: '8px 0' }} />
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 4 }}>
    <span style={{ color: T.muted }}>{label}</span>
    <span style={{ fontWeight: 600, color: T.text }}>{value}</span>
  </div>
);

// ── renderDetail helper ───────────────────────────────────────────────────────

const formatLabel = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();

const isURL = (val) => {
  if (typeof val !== 'string') return false;
  try { new URL(val); return true; } catch { return false; }
};

const isImageURL = (val) =>
  typeof val === 'string' && /\.(jpeg|jpg|gif|png|svg|webp)$/i.test(val);

function renderDetail(label, value, depth = 0) {
  if (!label) return null;
  const fLabel = formatLabel(label);

  let content;
  if (value === null || value === undefined || value === '') {
    content = <span style={{ color: T.muted }}>—</span>;
  } else if (isImageURL(value)) {
    content = (
      <img src={value} alt={fLabel} style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: `1px solid ${T.border}` }} />
    );
  } else if (isURL(value)) {
    content = (
      <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: T.primary, fontWeight: 600, fontSize: 11, textDecoration: 'underline', wordBreak: 'break-all' }}>
        {value}
      </a>
    );
  } else if (typeof value === 'boolean') {
    content = <Badge variant={value ? 'success' : 'danger'}>{value ? 'Yes' : 'No'}</Badge>;
  } else if (typeof value === 'object') {
    content = (
      <div style={{ marginTop: 6, paddingLeft: 8, borderLeft: `2px solid ${T.border}` }}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 6 }}>{renderDetail(k, v, depth + 1)}</div>
        ))}
      </div>
    );
  } else {
    content = <span style={{ color: T.text, fontWeight: 600 }}>{value.toString()}</span>;
  }

  return (
    <div style={{ marginBottom: depth > 0 ? 6 : 0 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
        {fLabel}
      </div>
      <div style={{ fontSize: 12 }}>{content}</div>
    </div>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

const CartSkeleton = () => (
  <div style={{ padding: 16, minWidth: 320 }}>
    <div className="ro-skeleton" style={{ height: 20, width: '50%', marginBottom: 16 }} />
    {[80, 100, 90].map((w, i) => (
      <div key={i} className="ro-skeleton" style={{ height: 13, width: `${w}%`, marginBottom: 10 }} />
    ))}
    <div className="ro-skeleton" style={{ height: 13, width: '70%' }} />
  </div>
);

// ── Order response card ───────────────────────────────────────────────────────

const ResponseCard = ({ response }) => {
  const isSuccess = response?.newOrderDetail?.status === 'success';
  const accentColor = isSuccess ? T.success : T.danger;
  const accentBg = isSuccess ? T.successLight : T.dangerLight;

  return (
    <SectionCard title="Order Result">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 18px',
        background: accentBg,
        borderRadius: 10,
        border: `1px solid ${accentColor}30`,
        marginBottom: 16,
      }}>
        <span style={{ fontSize: 28 }}>{isSuccess ? '✅' : '❌'}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: accentColor }}>{isSuccess ? 'Order Placed Successfully' : 'Order Failed'}</div>
          {response.message && <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{response.message}</div>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {response.failedOrderDetail && (
          <div style={{ background: T.surface, borderRadius: 10, padding: 16, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>Failed Order</div>
            <InfoRow label="Order ID" value={response.failedOrderDetail.orderId?.toString() ?? '—'} />
            <InfoRow label="Replaced" value={response.failedOrderDetail.replaced?.toString() ?? '—'} />
            <InfoRow label="Cancelled" value={response.failedOrderDetail.cancelled?.toString() ?? 'NULL'} />
            <InfoRow label="Refund Required" value={response.failedOrderDetail.refundRequired?.toString() ?? 'NULL'} />
            <InfoRow label="Refunded" value={response.failedOrderDetail.refunded?.toString() ?? 'NULL'} />
          </div>
        )}
        {response.newOrderDetail && (
          <div style={{ background: T.surface, borderRadius: 10, padding: 16, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>New Order</div>
            <InfoRow label="Status" value={response.newOrderDetail.status?.toString() ?? '—'} />
            <InfoRow label="Order ID" value={response.newOrderDetail.orderId?.toString() ?? '—'} />
            <InfoRow label="Cancelled" value={response.newOrderDetail.cancelled?.toString() ?? 'NULL'} />
            <InfoRow label="Refund Required" value={response.newOrderDetail.refundRequired?.toString() ?? 'NULL'} />
            <InfoRow label="Refunded" value={response.newOrderDetail.refunded?.toString() ?? 'NULL'} />
          </div>
        )}
      </div>
    </SectionCard>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const RetryOrder = ({ onClose, orderId = null, ...props }) => {
  const [digitisationId, setDigitisationId]                       = useState(props.digitisationId || null);
  const [cartData, setCartData]                                   = useState([]);
  const [policy, setPolicy]                                       = useState({});
  const [cartPartnerList, setCartPartnerList]                     = useState([]);
  const [selectedCartIndex, setSelectedCartIndex]                 = useState(null);
  const [isLoading, setIsLoading]                                 = useState(false);
  const [selectedCart, setSelectedCart]                           = useState(null);
  const [partnerOptions, setPartnerOptions]                       = useState(['All']);
  const [selectedPartner, setSelecetdPartner]                     = useState('All');
  const [isCartUpdated, setIsCartUpdated]                         = useState(false);
  const [updatedCarts, setUpdatedCarts]                           = useState([]);
  const [retryOrderResponse, setRetryOrderResponse]               = useState(null);
  const [isAnyUnaddedSubstitute, setIsAnyUnaddedSubstitute]       = useState(false);
  const [isReplacementForCancelledOrder, setIsReplacement]        = useState(false);
  const [apolloPickupStores, setApolloPickupStores]               = useState([]);
  const [showPickupStores, setShowPickupStores]                   = useState(false);
  const [selectedStore, setSelectedStore]                         = useState(null);
  const [isApolloCartSelected, setIsApolloCartSelected]           = useState(false);
  const [deliveryOption, setDeliveryOption]                       = useState(null);
  const [isHomeDeliveryAvailable, setIsHomeDeliveryAvailable]     = useState(false);
  const [reOrderLevel, setReOrderLevel]                           = useState(null);

  const handleCheckboxChange = (e) => setIsReplacement(e.target.checked);

  useEffect(() => {
    async function getDigitisationIdOfOrderID() {
      const response = await getDigitisationId(orderId);
      if (response && response.status && response.status === 'success') {
        setDigitisationId(response.digitisationId);
        const cartResponse = await getCartsFromDigitisationId(response.digitisationId);
        if (cartResponse && cartResponse.carts) {
          setCartData(cartResponse.carts);
          setCartPartnerList(cartResponse.partners);
          setPolicy(cartResponse.policy);
          setReOrderLevel(cartResponse.reOrderLevel ?? null);
          setPartnerOptions((prev) => [...new Set([...prev, ...cartResponse.carts.map((c) => c.partnerName)])]);
        } else {
          onClose();
        }
      }
    }
    if (orderId) getDigitisationIdOfOrderID();
  }, [orderId]);

  useEffect(() => {
    if (selectedCart) {
      const cur = cartData.find((cart) => cart.cartId === selectedCart);
      setIsAnyUnaddedSubstitute(cur.medicines.some((m) => m.isSubstitute && !m.isSubstituteAdded));
    }
  }, [selectedCart, cartData]);

  const handleCartSelection = async (index, cartId) => {
    const selected = cartData.find((c) => c.cartId === cartId);
    if (selected && selected.partnerName === 'Apollo') {
      setIsApolloCartSelected(true);
      try {
        const response = await getDetailsForApollocart(cartId);
        if (response && response.message === 'success') {
          setIsHomeDeliveryAvailable(response.isHomeDeliveryAvailable || false);
        }
      } catch {
        setIsHomeDeliveryAvailable(false);
      }
      setSelectedCartIndex(index);
      setSelectedCart(cartId);
    } else {
      setIsApolloCartSelected(false);
      setShowPickupStores(false);
      setDeliveryOption(null);
      setSelectedStore(null);
      setIsHomeDeliveryAvailable(false);
      setSelectedCartIndex(index);
      setSelectedCart(cartId);
    }
  };

  useEffect(() => {
    async function setCart() {
      const cartResponse = await getCartsFromDigitisationId(digitisationId);
      if (cartResponse && cartResponse.carts) {
        setCartData(cartResponse.carts);
        setCartPartnerList(cartResponse.partners);
        setPolicy(cartResponse.policy);
        setReOrderLevel(cartResponse.reOrderLevel ?? null);
        setPartnerOptions((prev) => [...new Set([...prev, ...cartResponse.carts.map((c) => c.partnerName)])]);
      } else {
        alert(`NOTE: ${cartResponse.message}`);
        onClose();
      }
    }
    if (!orderId && digitisationId) setCart();
  }, [digitisationId]);

  const modifyAndGetUpdatedCarts = async () => {
    try {
      setIsLoading(true);
      await Promise.all(
        updatedCarts.map((cartId) =>
          modifyCartData(cartId, cartData.find((c) => c.cartId === cartId).medicines || null)
        )
      );
      const cartResponse = await getCartsFromDigitisationId(digitisationId);
      if (cartResponse && cartResponse.carts) {
        setCartData(cartResponse.carts);
        setCartPartnerList(cartResponse.partners);
        setPolicy(cartResponse.policy);
        setReOrderLevel(cartResponse.reOrderLevel ?? null);
        setPartnerOptions(['All', ...cartResponse.carts.map((c) => c.partnerName)]);
      }
      setIsCartUpdated(false);
      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
  };

  const decreaseQuantity = (cartIndex, medIndex) => {
    const updated = [...cartData];
    if (updated[cartIndex].medicines[medIndex].quantity > 0) {
      setIsCartUpdated(true);
      setUpdatedCarts((prev) => [...prev, updated[cartIndex].cartId]);
      updated[cartIndex].medicines[medIndex].quantity -= 1;
      setCartData(updated);
    }
  };

  const increaseQuantity = async (cartIndex, medIndex, maxQuant) => {
    if (cartData[cartIndex].medicines[medIndex].isSubstitute && !cartData[cartIndex].medicines[medIndex].isSubstituteAdded) {
      const ok = await addSubstituteToCart(cartData[cartIndex].cartId, cartData[cartIndex].medicines[medIndex].drugId);
      if (ok) await modifyAndGetUpdatedCarts();
    }
    const updated = [...cartData];
    if (updated[cartIndex].medicines[medIndex].quantity < maxQuant) {
      setIsCartUpdated(true);
      setUpdatedCarts((prev) => [...prev, updated[cartIndex].cartId]);
      updated[cartIndex].medicines[medIndex].quantity += 1;
      setCartData(updated);
    }
  };

  const placeOrder = async (cartId) => {
    if (isApolloCartSelected && deliveryOption === 'pick-up' && !showPickupStores) {
      const res = await getPartnerStoresByCartId(cartId);
      if (res && res.success) {
        setApolloPickupStores((res.storeLists || {}).pickUpStores || []);
        setShowPickupStores(true);
        return;
      }
    }
    const res = orderId
      ? await placeFailedOrderCart(orderId, cartId)
      : await placeOrderByCartId(cartId, isReplacementForCancelledOrder, selectedStore);
    setRetryOrderResponse(res);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const filteredCarts = cartData.filter(
    (cart) => selectedPartner === 'All' || cart.partnerName === selectedPartner
  );

  return (
    <Modal
      isOpen={true}
      style={{ maxWidth: 1200, width: '100%', borderRadius: 16 }}
    >
      <style>{SHIMMER_CSS}</style>

      {/* ── Modal header ── */}
      <ModalHeader
        toggle={onClose}
        style={{
          background: T.primaryLight,
          borderBottom: `1px solid ${T.primaryBorder}`,
          padding: '18px 24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: T.primary, boxShadow: `0 0 0 3px ${T.primaryBorder}` }} />
          <span style={{ fontSize: 17, fontWeight: 700, color: T.text }}>Get Carts & Place Order</span>
          {digitisationId && (
            <span style={{
              background: T.white,
              border: `1.5px solid ${T.primaryBorder}`,
              color: T.primary,
              fontSize: 11,
              fontWeight: 700,
              padding: '3px 10px',
              borderRadius: 100,
              letterSpacing: '0.04em',
            }}>
              DIG-ID: {digitisationId}
            </span>
          )}
        </div>
      </ModalHeader>

      {/* ── Modal body ── */}
      <ModalBody style={{ padding: '24px', background: T.pageBg, maxHeight: '85vh', overflowY: 'auto' }}>

        {/* Policy Details */}
        {Object.keys(policy).length > 0 && (
          <SectionCard title="Policy Details">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 20 }}>
              {Object.entries(policy).map(([key, value]) => (
                <div key={key}>{renderDetail(key, value)}</div>
              ))}
            </div>
            {reOrderLevel != null && (
              <>
                <Divider />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Re-Order Level</span>
                  <Badge variant="primary">{reOrderLevel}</Badge>
                </div>
              </>
            )}
          </SectionCard>
        )}

        {/* Digitized Partners */}
        {Object.values(cartPartnerList).length > 0 && (
          <SectionCard title="Digitized Partners">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {Object.values(cartPartnerList).map((partner, index) => {
                const details = Object.entries(partner).filter(([k]) => k !== 'partnerName' && k !== 'partnerLogoUrl');
                return (
                  <div
                    key={index}
                    style={{
                      borderRadius: 10,
                      border: `1px solid ${T.border}`,
                      background: T.white,
                      overflow: 'hidden',
                      boxShadow: T.shadow,
                    }}
                  >
                    {/* Partner name header */}
                    <div style={{
                      padding: '9px 12px',
                      background: T.primaryLight,
                      borderBottom: `1px solid ${T.primaryBorder}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}>
                      {partner.partnerLogoUrl && (
                        <img
                          src={partner.partnerLogoUrl}
                          alt={partner.partnerName}
                          style={{ width: 22, height: 22, borderRadius: 4, objectFit: 'contain', background: T.white, border: `1px solid ${T.primaryBorder}`, padding: 1, flexShrink: 0 }}
                        />
                      )}
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {partner.partnerName || `Partner ${index + 1}`}
                      </span>
                    </div>
                    {/* Compact key-value rows */}
                    {details.length > 0 && (
                      <div style={{ padding: '8px 12px' }}>
                        {details.map(([k, v]) => {
                          const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
                          let display;
                          if (v === null || v === undefined || v === '') {
                            display = <span style={{ color: T.muted }}>—</span>;
                          } else if (typeof v === 'boolean') {
                            display = <Badge variant={v ? 'success' : 'danger'}>{v ? 'Yes' : 'No'}</Badge>;
                          } else if (isImageURL(String(v))) {
                            display = <img src={v} alt={label} style={{ width: 24, height: 24, objectFit: 'cover', borderRadius: 4, border: `1px solid ${T.border}` }} />;
                          } else if (isURL(String(v))) {
                            display = <a href={v} target="_blank" rel="noopener noreferrer" style={{ color: T.primary, fontSize: 10, textDecoration: 'underline', wordBreak: 'break-all' }}>{v}</a>;
                          } else if (typeof v === 'object') {
                            display = <span style={{ color: T.muted, fontStyle: 'italic', fontSize: 10 }}>{JSON.stringify(v)}</span>;
                          } else {
                            display = <span style={{ color: T.text, fontWeight: 600 }}>{v.toString()}</span>;
                          }
                          return (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: `1px solid ${T.border}` }}>
                              <span style={{ fontSize: 10, color: T.muted, fontWeight: 600, textTransform: 'capitalize', flexShrink: 0 }}>{label}</span>
                              <span style={{ fontSize: 11 }}>{display}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* Carts */}
        {cartData.length > 0 && (
          <SectionCard title="Available Carts">
            {/* Partner filter pills */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {partnerOptions.map((partner, index) => {
                const active = selectedPartner === partner;
                return (
                  <button
                    key={index}
                    onClick={() => setSelecetdPartner(partner)}
                    style={{
                      padding: '5px 16px',
                      borderRadius: 100,
                      border: `1.5px solid ${active ? T.primary : T.border}`,
                      background: active ? T.primaryLight : T.white,
                      color: active ? T.primary : T.muted,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {partner}
                  </button>
                );
              })}
            </div>

            {/* Horizontal cart scroll */}
            <div style={{ display: 'flex', overflowX: 'auto', gap: 16, paddingBottom: 8 }}>
              {filteredCarts.map((cart, cartIndex) => {
                const isSelected = selectedCartIndex === cartIndex;
                const isUpdating = isLoading && updatedCarts.includes(cart.cartId);

                return (
                  <div
                    key={cart.cartId}
                    onClick={() => handleCartSelection(cartIndex, cart.cartId)}
                    style={{
                      minWidth: 320,
                      maxWidth: 360,
                      flexShrink: 0,
                      borderRadius: 12,
                      border: `2px solid ${isSelected ? T.primary : T.border}`,
                      background: T.white,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      boxShadow: isSelected ? `0 0 0 4px ${T.primaryLight}, ${T.shadow}` : T.shadow,
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {isUpdating ? (
                      <CartSkeleton />
                    ) : (
                      <>
                        {/* Card header */}
                        <div style={{
                          padding: '12px 16px',
                          background: isSelected ? T.primaryLight : T.surface,
                          borderBottom: `1px solid ${isSelected ? T.primaryBorder : T.border}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: isSelected ? T.primary : T.text, textTransform: 'capitalize' }}>
                            {cart.partnerName}
                          </span>
                          {/* Custom radio indicator */}
                          <div style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: `2px solid ${isSelected ? T.primary : T.border}`,
                            background: isSelected ? T.primary : T.white,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s',
                          }}>
                            {isSelected && <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.white }} />}
                          </div>
                        </div>

                        {/* Medicine list */}
                        <div style={{ flexGrow: 1, overflowY: 'auto', maxHeight: 280 }}>
                          {cart.medicines.map((med, medIndex) => {
                            const isCovered = med.isCovered !== 0;
                            const qty = med.isSubstitute && !med.isSubstituteAdded ? 0 : med.quantity;
                            let qtyBg = T.white, qtyColor = T.text;
                            if (med.isSubstitute) { qtyBg = T.successLight; qtyColor = T.success; }
                            if (!isCovered) { qtyBg = T.warningLight; qtyColor = T.warning; }

                            return (
                              <div
                                key={med.cartItemId}
                                style={{
                                  padding: '8px 14px',
                                  borderBottom: `1px solid ${T.border}`,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 5,
                                }}
                              >
                                {/* Row 1: name | qty controls | price — grid keeps columns intact */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr auto auto',
                                  alignItems: 'center',
                                  columnGap: 8,
                                }}>
                                  <div style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: T.text,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {med.brandName}
                                  </div>

                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <QtyBtn onClick={(e) => { e.stopPropagation(); decreaseQuantity(cartIndex, medIndex); }}>{'−'}</QtyBtn>
                                    <span style={{
                                      width: 28,
                                      textAlign: 'center',
                                      padding: '3px 4px',
                                      borderRadius: 6,
                                      background: qtyBg,
                                      color: qtyColor,
                                      fontSize: 12,
                                      fontWeight: 700,
                                      border: `1.5px solid ${T.border}`,
                                      display: 'inline-block',
                                    }}>
                                      {qty}
                                    </span>
                                    <QtyBtn primary onClick={(e) => { e.stopPropagation(); increaseQuantity(cartIndex, medIndex, med.maxQuantity); }}>{'+'}</QtyBtn>
                                  </div>

                                  <div style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 38, textAlign: 'right' }}>
                                    {'₹'}{med.mrp}
                                  </div>
                                </div>

                                {/* Row 2: badges — ternary avoids rendering 0 when field is numeric falsy */}
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                  {med.isSubstitute ? <Badge variant="primary">Substitute</Badge> : null}
                                  {med.isSubstitute ? (
                                    med.isSubstituteAdded
                                      ? <Badge variant="success">Sub Added</Badge>
                                      : <Badge variant="warning">Sub Pending</Badge>
                                  ) : null}
                                  {med.isAvailable != null ? (
                                    med.isAvailable
                                      ? <Badge variant="success">Available</Badge>
                                      : <Badge variant="danger">Unavailable</Badge>
                                  ) : null}
                                  <Badge variant={isCovered ? 'success' : 'warning'}>
                                    {isCovered ? 'Covered' : 'Uncovered'}
                                  </Badge>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Pricing footer */}
                        <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.border}`, background: T.surface }}>
                          <InfoRow label="Subtotal" value={`₹${cart.orderSubTotal}`} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: T.muted }}>Visit Discount</span>
                            <span style={{ fontWeight: 600, color: T.success }}>−₹{cart.orderDiscount}</span>
                          </div>
                          {cart.walletDiscount > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                              <span style={{ color: T.muted }}>Wallet Discount</span>
                              <span style={{ fontWeight: 600, color: T.success }}>−₹{cart.walletDiscount}</span>
                            </div>
                          )}
                          <Divider />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
                            <span>Total</span>
                            <span>₹{cart.totalAmount}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                            <span style={{ color: T.muted }}>User Payable</span>
                            <span style={{ fontWeight: 700, color: T.primary }}>₹{cart.userPay}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                            <span style={{ color: T.muted }}>Order Type</span>
                            <span style={{ color: T.muted, fontWeight: 600 }}>{cart.orderType || 'code'}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        {/* ── Bottom action area ── */}
        {cartData.length > 0 && (
          retryOrderResponse ? (
            <ResponseCard response={retryOrderResponse} />
          ) : isApolloCartSelected && !showPickupStores ? (
            /* Delivery option picker */
            <SectionCard title="Delivery Option">
              <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                {isHomeDeliveryAvailable && (
                  <label
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 18px',
                      borderRadius: 10,
                      border: `2px solid ${deliveryOption === 'home-delivery' ? T.primary : T.border}`,
                      background: deliveryOption === 'home-delivery' ? T.primaryLight : T.white,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="deliveryOption"
                      checked={deliveryOption === 'home-delivery'}
                      onChange={() => setDeliveryOption('home-delivery')}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${deliveryOption === 'home-delivery' ? T.primary : T.border}`,
                      background: deliveryOption === 'home-delivery' ? T.primary : T.white,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      {deliveryOption === 'home-delivery' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.white }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: deliveryOption === 'home-delivery' ? T.primary : T.text }}>🏠 Home Delivery</div>
                      <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Deliver to registered address</div>
                    </div>
                  </label>
                )}
                <label
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 18px',
                    borderRadius: 10,
                    border: `2px solid ${deliveryOption === 'pick-up' ? T.primary : T.border}`,
                    background: deliveryOption === 'pick-up' ? T.primaryLight : T.white,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="deliveryOption"
                    checked={deliveryOption === 'pick-up'}
                    onChange={() => setDeliveryOption('pick-up')}
                    style={{ display: 'none' }}
                  />
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%',
                    border: `2px solid ${deliveryOption === 'pick-up' ? T.primary : T.border}`,
                    background: deliveryOption === 'pick-up' ? T.primary : T.white,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {deliveryOption === 'pick-up' && <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.white }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: deliveryOption === 'pick-up' ? T.primary : T.text }}>🏪 Store Pickup</div>
                    <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>Pick up from nearby store</div>
                  </div>
                </label>
              </div>

              {!isHomeDeliveryAvailable && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 8,
                  background: T.warningLight, border: `1px solid ${T.warning}30`,
                  marginBottom: 16, fontSize: 12, color: T.warning, fontWeight: 500,
                }}>
                  ⚠️ Home delivery is not available for this location.
                </div>
              )}

              <PrimaryBtn
                disabled={!deliveryOption}
                onClick={() => isCartUpdated ? modifyAndGetUpdatedCarts() : placeOrder(selectedCart)}
              >
                {isCartUpdated ? 'Refresh Billing' : deliveryOption === 'pick-up' ? 'Continue to Store Selection' : 'Place Order'}
              </PrimaryBtn>
            </SectionCard>
          ) : showPickupStores ? (
            /* Store picker */
            <SectionCard title="Select Pickup Store">
              {apolloPickupStores.length === 0 ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 16px', borderRadius: 8,
                  background: T.dangerLight, border: `1px solid ${T.danger}30`,
                  fontSize: 13, color: T.danger, fontWeight: 500, marginBottom: 16,
                }}>
                  ❌ No pickup stores available for this location.
                </div>
              ) : (
                <div style={{
                  border: `1px solid ${T.border}`,
                  borderRadius: 10,
                  overflow: 'hidden',
                  marginBottom: 20,
                }}>
                  {apolloPickupStores.map((store, index) => {
                    const isActive = selectedStore === store.partnerStoreId;
                    return (
                      <label
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 14,
                          padding: '14px 18px',
                          borderBottom: index !== apolloPickupStores.length - 1 ? `1px solid ${T.border}` : 'none',
                          background: isActive ? T.primaryLight : T.white,
                          cursor: 'pointer',
                          transition: 'background 0.1s',
                        }}
                      >
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%', marginTop: 2,
                          border: `2px solid ${isActive ? T.primary : T.border}`,
                          background: isActive ? T.primary : T.white,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          cursor: 'pointer',
                        }}
                          onClick={() => setSelectedStore(store.partnerStoreId)}
                        >
                          {isActive && <div style={{ width: 7, height: 7, borderRadius: '50%', background: T.white }} />}
                        </div>
                        <input
                          type="radio"
                          name="pickupStore"
                          value={store.partnerStoreId}
                          checked={isActive}
                          onChange={() => setSelectedStore(store.partnerStoreId)}
                          style={{ display: 'none' }}
                        />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: isActive ? T.primary : T.text }}>
                              {store.storeName}
                            </span>
                            <span style={{ fontSize: 11, color: T.muted }}>ID: {store.partnerStoreId}</span>
                            {store.isPickupOrderPlacedBefore && (
                              <Badge variant="blue">Selected By User</Badge>
                            )}
                          </div>
                          {store.address && (
                            <div style={{ fontSize: 12, color: T.muted, lineHeight: '1.5' }}>{store.address}</div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <PrimaryBtn
                disabled={isLoading || selectedCartIndex === null || isAnyUnaddedSubstitute || (apolloPickupStores.length > 0 && selectedStore === null)}
                onClick={() => isCartUpdated ? modifyAndGetUpdatedCarts() : placeOrder(selectedCart)}
              >
                {isCartUpdated ? 'Refresh Billing' : 'Place Order'}
              </PrimaryBtn>
            </SectionCard>
          ) : (
            /* Default place order section */
            <div style={{
              background: T.white,
              borderRadius: 12,
              border: `1px solid ${T.border}`,
              boxShadow: T.shadow,
              padding: 20,
            }}>
              {!isLoading && isAnyUnaddedSubstitute && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '11px 16px',
                  borderRadius: 8,
                  background: T.dangerLight,
                  border: `1px solid ${T.danger}30`,
                  marginBottom: 16,
                  fontSize: 13,
                  color: T.danger,
                  fontWeight: 600,
                }}>
                  ⚠️ Please add all substitutes before placing the order.
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isLoading ? 'not-allowed' : 'pointer' }}>
                  <div
                    onClick={() => !isLoading && setIsReplacement(!isReplacementForCancelledOrder)}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 5,
                      border: `2px solid ${isReplacementForCancelledOrder ? T.primary : T.border}`,
                      background: isReplacementForCancelledOrder ? T.primary : T.white,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.15s',
                    }}
                  >
                    {isReplacementForCancelledOrder && (
                      <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                        <path d="M1 4L4 7L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={isReplacementForCancelledOrder}
                    onChange={handleCheckboxChange}
                    disabled={isLoading}
                    style={{ display: 'none' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>Is Replacement for Cancelled Order</span>
                </label>

                <PrimaryBtn
                  disabled={isLoading || selectedCartIndex === null || isAnyUnaddedSubstitute}
                  onClick={() => isCartUpdated ? modifyAndGetUpdatedCarts() : placeOrder(selectedCart)}
                >
                  {isLoading ? 'Processing…' : isCartUpdated ? 'Refresh Billing' : 'Place Order'}
                </PrimaryBtn>
              </div>
            </div>
          )
        )}
      </ModalBody>
    </Modal>
  );
};

export default RetryOrder;
